// @spec GHG-STR-018
'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useGHGSettings, useUpdateGHGSettings } from '@/features/ghg/api/use-ghg-settings';
import type { Scope2CoverageKey } from '@/features/ghg/types/ghg-settings';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/ghg/settings/scope2')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.scope2Settings'),
  }),
  component: Scope2SettingsPage,
});

function Scope2SettingsPage() {
  const { t } = useTranslation('ghg');
  const { settings, isLoading } = useGHGSettings();
  const { mutate, isPending } = useUpdateGHGSettings();
  const [assumptions, setAssumptions] = useState('');
  const [comments, setComments] = useState('');

  useEffect(() => {
    if (settings?.scope2Notes) {
      setAssumptions(settings.scope2Notes.assumptions);
      setComments(settings.scope2Notes.comments);
    }
  }, [settings?.scope2Notes]);

  const handleToggle = (key: Scope2CoverageKey, checked: boolean) => {
    mutate({ scope2Coverage: { [key]: { enabled: checked } } });
  };

  const handleSave = () => {
    mutate(
      { scope2Notes: { assumptions, comments } },
      { onSuccess: () => toast.success(t('toast.scope2SettingsSaved')) }
    );
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-3xl space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-4 w-96" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  const coverage = settings?.scope2Coverage;

  return (
    <div className="p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('settingsPage.scope2Heading')}</h1>
          <p className="mt-2 text-muted-foreground">
            {t('settingsPage.scope2Description')}
          </p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>{t('settingsPage.scope2Coverage')}</CardTitle>
            <CardDescription>
              {t('settingsPage.scope2CoverageDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {([
                {
                  key: 'electricity' as const,
                  label: t('settingsPage.electricity'),
                  description: t('settingsPage.electricityDesc'),
                },
                {
                  key: 'heat' as const,
                  label: t('settingsPage.heat'),
                  description: t('settingsPage.heatDesc'),
                },
                {
                  key: 'cooling' as const,
                  label: t('settingsPage.cooling'),
                  description: t('settingsPage.coolingDesc'),
                },
                {
                  key: 'steam' as const,
                  label: t('settingsPage.steam'),
                  description: t('settingsPage.steamDesc'),
                },
              ]).map((item) => (
                <div
                  key={item.key}
                  className="flex items-start justify-between gap-4 rounded-lg border p-3"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`scope2-${item.key}`}
                        checked={coverage?.[item.key]?.enabled ?? true}
                        onCheckedChange={(checked) => {
                          handleToggle(item.key, checked);
                        }}
                      />
                      <Label htmlFor={`scope2-${item.key}`} className="cursor-pointer font-medium">
                        {item.label}
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('settingsPage.assumptions')}</CardTitle>
            <CardDescription>
              {t('settingsPage.scope2AssumptionsDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={assumptions}
              onChange={(event) => {
                setAssumptions(event.target.value);
              }}
              placeholder={t('settingsPage.scope2AssumptionsPlaceholder')}
              rows={6}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('settingsPage.additionalComments')}</CardTitle>
            <CardDescription>
              {t('settingsPage.scope2CommentsDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={comments}
              onChange={(event) => {
                setComments(event.target.value);
              }}
              placeholder={t('settingsPage.commentsPlaceholder')}
              rows={6}
            />
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isPending}>
            {t('settingsPage.saveSettings')}
          </Button>
        </div>
      </div>
    </div>
  );
}
